import { BaseItemResponse } from "../../response/BaseItemResponse";
import { errorMessageComponent } from "../../components/error/ErrorMessageComponent";
import { IListCache } from "./IListCache";
import { serviceSync } from "./ServiceSync";

/** Base de los listados en memoria: trae la lista completa por gRPC-Web (`fetchItems`) y la cachea en el servicio. */
export abstract class ServiceGetListBase<TItem extends BaseItemResponse> implements IListCache<TItem> {
  /** Clave única del listado (constantes `SYNC_KEY_*`): deduplica `sync()`. Los listados por scope
   *  (p. ej. por cliente) la arman con el id del scope. */
  protected abstract readonly syncKey: string;
  protected readonly sortBy?: string;

  private _items: TItem[] = [];

  /** Copia de la última lista traída del servidor; vacía hasta el primer `sync()`. */
  get items(): TItem[] {
    return [...this._items];
  }

  /**
   * Trae la lista completa del servidor y la deja en memoria. Se deduplica por
   * `syncKey`: dos llamadas simultáneas comparten un solo request.
   */
  sync(): Promise<void> {
    return serviceSync.run(this.syncKey, async () => {
      const items = await this.fetchItems();
      // Sin store local que podar, los registros borrados hay que filtrarlos acá.
      this._items = items.filter((item) => !item.isEliminado);
    });
  }

  /** Llamada gRPC-Web del listado + adaptador `to*Response()` al tipo de la web. La deduplicación,
   *  el filtro de `isEliminado`, el orden y `upsert/remove` quedan en la base. */
  protected abstract fetchItems(): Promise<TItem[]>;

  /**
   * Sincroniza y devuelve una copia (ordenada por `sortBy` si lo hay); ante error avisa, escribe el error en consola y
   * devuelve lo último cargado. Nunca rechaza: los buscadores de clientes dependen de que siempre haya una lista.
   */
  async get(): Promise<TItem[]> {
    await this.sync().catch((error) => {
      console.warn(`[ServiceGetListBase.get] no se pudo actualizar la lista "${this.syncKey}"; queda la última cargada`, error);
      errorMessageComponent.addWarning("Sin conexión", "No se pudo actualizar la lista.");
    });
    // Copia siempre: quien llama suele ordenar o recortar el resultado en sitio.
    return this.sortBy ? this._sort(this._items, this.sortBy) : [...this._items];
  }

  // ── IListCache: lo usan los servicios de save/delete ──────────────────────────

  /** Reemplaza el item con el mismo `id` o lo agrega al final. */
  upsert(item: TItem): void {
    const index = this._items.findIndex((x) => x.id === item.id);
    if (index >= 0) this._items[index] = item;
    else this._items.push(item);
  }

  /** Quita de la lista en memoria el item con ese `id`. */
  remove(id: string): void {
    this._items = this._items.filter((x) => x.id !== id);
  }

  //

  /** Copia ordenada por `key` con collator insensible a mayúsculas y acentos. */
  private _sort(items: TItem[], key: string): TItem[] {
    const collator = new Intl.Collator(undefined, { sensitivity: "base" });
    return [...items].sort((a, b) =>
      collator.compare(
        String((a as unknown as Record<string, unknown>)[key] ?? ""),
        String((b as unknown as Record<string, unknown>)[key] ?? ""),
      ),
    );
  }
}
